import React, { useEffect, useState } from "react";
import apiService from "../services/apiService";
import { ENVIRONMENTS, ENV_STORAGE_KEY } from "../config/environments";
import { switchEnvironment } from "../services/environmentSwitcher";

const LoginPage = ({ onLoginSuccess }) => {
	const [token, setToken] = useState("");
	const [status, setStatus] = useState({ message: "", type: "", show: false });
	const [isLoading, setIsLoading] = useState(false);

	const savedProxy = (() => { try { return localStorage.getItem(ENV_STORAGE_KEY); } catch (_) { return null; } })();
	const [activeEnv, setActiveEnv] = useState(
		ENVIRONMENTS.find((e) => e.proxy === savedProxy)?.key ?? "dev-int"
	);

	const handleEnvChange = (env) => {
		setActiveEnv(env.key);
		switchEnvironment(env.proxy);
	};

	useEffect(() => {
		if (apiService.isAuthenticated()) {
			showStatus("Already authenticated. Loading dashboard...", "success");
			setTimeout(() => onLoginSuccess(apiService.getUser()), 1000);
		}
	}, []);

	const handleSubmit = (e) => {
		e.preventDefault();
		const trimmed = token.trim();
		if (!trimmed) {
			showStatus("Please paste your bearer token.", "error");
			return;
		}

		setIsLoading(true);
		const stored = apiService.storeToken(trimmed);
		if (!stored) {
			showStatus("Failed to store token. Please try again.", "error");
			setIsLoading(false);
			return;
		}

		const user = apiService.getUser();
		localStorage.setItem("isAuthenticated", "true");
		if (user) localStorage.setItem("userData", JSON.stringify(user));

		showStatus("Authenticated! Loading dashboard...", "success");
		setTimeout(() => onLoginSuccess(user), 1000);
	};

	const showStatus = (message, type) => {
		setStatus({ message, type, show: true });
		if (type === "success") {
			setTimeout(() => setStatus((prev) => ({ ...prev, show: false })), 3000);
		}
	};

	return (
		<div
			style={{
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px",
			}}
		>
			<div
				style={{
					background: "white",
					borderRadius: "20px",
					boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
					overflow: "hidden",
					maxWidth: "500px",
					width: "100%",
					textAlign: "center",
				}}
			>
				{/* Header */}
				<div
					style={{
						background: "linear-gradient(135deg, #0076bd 0%, #1fbab0 100%)",
						color: "white",
						padding: "50px 30px",
					}}
				>
					<h1
						style={{
							fontSize: "2.5em",
							marginBottom: "10px",
							fontWeight: "300",
						}}
					>
						Discover Inventory
					</h1>
					<p style={{ opacity: "0.9", fontSize: "1.1em" }}>
						Find and select relevant inventory using natural language search
					</p>
				</div>

				{/* Form */}
				<div style={{ padding: "40px 30px" }}>
					{status.show && (
						<div
							style={{
								padding: "15px",
								borderRadius: "8px",
								marginBottom: "20px",
								background: status.type === "error" ? "#f8d7da" : "#d4edda",
								border: `1px solid ${status.type === "error" ? "#f5c6cb" : "#c3e6cb"}`,
								color: status.type === "error" ? "#721c24" : "#155724",
							}}
						>
							{status.message}
						</div>
					)}

					<h2 style={{ marginBottom: "8px", color: "#343a40" }}>Sign In</h2>
					<p style={{ marginBottom: "20px", color: "#6c757d", fontSize: "0.95em" }}>
						Paste your bearer token to access the dashboard
					</p>

					{/* Environment selector */}
					<div style={{ marginBottom: "24px" }}>
						<div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#555", marginBottom: "8px", textAlign: "left" }}>
							Environment
						</div>
						<div style={{ display: "flex", background: "#f1f3f5", borderRadius: "8px", padding: "3px", gap: "3px" }}>
							{ENVIRONMENTS.map((env) => {
								const isActive = activeEnv === env.key;
								return (
									<button
										key={env.key}
										type="button"
										onClick={() => handleEnvChange(env)}
										style={{
											flex: 1,
											padding: "8px 4px",
											borderRadius: "6px",
											border: "none",
											cursor: "pointer",
											fontSize: "0.76rem",
											whiteSpace: "nowrap",
											fontWeight: isActive ? 700 : 400,
											background: isActive
												? env.key === "prod"            ? "#dc2626"
												: env.key.startsWith("qa")      ? "#d97706"
												: "#0076bd"
												: "transparent",
											color: isActive ? "#fff" : "#666",
											transition: "all 0.15s",
										}}
									>
										{env.label}
									</button>
								);
							})}
						</div>
					</div>

					<form onSubmit={handleSubmit}>
						<textarea
							value={token}
							onChange={(e) => setToken(e.target.value)}
							placeholder="Paste your bearer token here..."
							rows={5}
							style={{
								width: "100%",
								padding: "12px",
								borderRadius: "8px",
								border: "1px solid #ced4da",
								fontSize: "0.85em",
								fontFamily: "monospace",
								resize: "vertical",
								boxSizing: "border-box",
								outline: "none",
								marginBottom: "16px",
								color: "#343a40",
							}}
							disabled={isLoading}
						/>
						<button
							type="submit"
							disabled={isLoading || !token.trim()}
							style={{
								width: "100%",
								padding: "14px",
								background:
									isLoading || !token.trim()
										? "#adb5bd"
										: "linear-gradient(135deg, #0076bd 0%, #1fbab0 100%)",
								color: "white",
								border: "none",
								borderRadius: "8px",
								fontSize: "1em",
								fontWeight: "600",
								cursor: isLoading || !token.trim() ? "not-allowed" : "pointer",
								transition: "opacity 0.2s",
							}}
						>
							{isLoading ? "Authenticating..." : "Sign In"}
						</button>
					</form>
				</div>

				{/* Footer */}
				<div
					style={{
						background: "#f8f9fa",
						padding: "20px",
						color: "#6c757d",
						fontSize: "14px",
					}}
				>
					<p>&copy; 2024 Discover Inventory | OpenX</p>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
